import { create } from "zustand";
import type { DataSource } from "../application/types";
import type { MeshSnapshot, NexusEvent } from "../domain/mesh";
import { resolveInitialLocale, type Locale } from "../i18n";

export type FocusState =
  { kind: "cosmos" } | { kind: "node"; nodeId: string } | { kind: "orbital"; nodeId: string; orbitalId: string };

interface NexusState {
  snapshot: MeshSnapshot | null;
  dataSource: DataSource;
  dataError: string | null;
  focus: FocusState;
  hoveredEntityId: string | null;
  events: NexusEvent[];
  activePulse: NexusEvent | null;
  commandOpen: boolean;
  chromeVisible: boolean;
  locale: Locale;
  idleMode: boolean;
  soundEnabled: boolean;
  portalTarget: string | null;
  easterEgg: boolean;
  accessibleMode: boolean;
  heroVisible: boolean;
  demoTourActive: boolean;
  hydrateSnapshot: (snapshot: MeshSnapshot, source: DataSource, error?: string | null) => void;
  activateNode: (id: string) => void;
  focusNode: (id: string) => void;
  focusOrbital: (nodeId: string, orbitalId: string) => void;
  focusCosmos: () => void;
  hoverEntity: (id: string | null) => void;
  ingestEvent: (event: NexusEvent) => void;
  recordDataStatus: (source: DataSource, error?: string | null) => void;
  setCommandOpen: (open: boolean) => void;
  toggleChrome: () => void;
  setLocale: (locale: Locale) => void;
  setIdleMode: (idle: boolean) => void;
  toggleSound: () => void;
  requestPortal: (href: string) => void;
  clearPortal: () => void;
  toggleEasterEgg: () => void;
  toggleAccessibleMode: () => void;
  setHeroVisible: (visible: boolean) => void;
  setDemoTourActive: (active: boolean) => void;
}

function mergeEvents(current: NexusEvent[], incoming: NexusEvent[]) {
  const result = [...current];
  const ids = new Set(current.map((event) => event.id));
  for (const event of incoming) {
    if (!ids.has(event.id)) result.push(event);
  }
  return result.sort((a, b) => Date.parse(b.timestamp) - Date.parse(a.timestamp)).slice(0, 40);
}

export const useNexusStore = create<NexusState>((set) => ({
  snapshot: null,
  dataSource: "fallback",
  dataError: null,
  focus: { kind: "cosmos" },
  hoveredEntityId: null,
  events: [],
  activePulse: null,
  commandOpen: false,
  chromeVisible: true,
  locale: resolveInitialLocale(),
  idleMode: false,
  soundEnabled: false,
  portalTarget: null,
  easterEgg: false,
  accessibleMode: false,
  heroVisible: true,
  demoTourActive: false,

  hydrateSnapshot: (snapshot, dataSource, dataError = null) =>
    set((state) => ({ snapshot, dataSource, dataError, events: mergeEvents(state.events, snapshot.events) })),
  activateNode: (id) => set({ focus: { kind: "node", nodeId: id }, commandOpen: false }),
  focusNode: (id) => set({ focus: { kind: "node", nodeId: id }, commandOpen: false }),
  focusOrbital: (nodeId, orbitalId) => set({ focus: { kind: "orbital", nodeId, orbitalId }, commandOpen: false }),
  focusCosmos: () => set({ focus: { kind: "cosmos" }, commandOpen: false }),
  hoverEntity: (hoveredEntityId) => set({ hoveredEntityId }),
  ingestEvent: (event) => set((state) => ({ activePulse: event, events: mergeEvents(state.events, [event]) })),
  recordDataStatus: (dataSource, dataError = null) => set({ dataSource, dataError }),
  setCommandOpen: (commandOpen) => set({ commandOpen }),
  toggleChrome: () => set((state) => ({ chromeVisible: !state.chromeVisible })),
  setLocale: (locale) => set({ locale }),
  setIdleMode: (idleMode) => set({ idleMode }),
  toggleSound: () => set((state) => ({ soundEnabled: !state.soundEnabled })),
  requestPortal: (portalTarget) => set({ portalTarget }),
  clearPortal: () => set({ portalTarget: null }),
  toggleEasterEgg: () => set((state) => ({ easterEgg: !state.easterEgg })),
  toggleAccessibleMode: () => set((state) => ({ accessibleMode: !state.accessibleMode, idleMode: false })),
  setHeroVisible: (heroVisible) => set({ heroVisible }),
  setDemoTourActive: (demoTourActive) => set({ demoTourActive }),
}));

export function focusedNodeId(focus: FocusState): string | null {
  return focus.kind === "cosmos" ? null : focus.nodeId;
}

export function focusedOrbitalId(focus: FocusState): string | null {
  return focus.kind === "orbital" ? focus.orbitalId : null;
}
export function focusTargetEntityId(focus: FocusState): string | null {
  if (focus.kind === "cosmos") return null;
  return focus.kind === "orbital" ? focus.orbitalId : focus.nodeId;
}
